import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";

import { copiarTexto } from "../utils/clipboard";
import muiTheme from "../theme/muiTheme";

const PROCESSOS = [
  {
    value: "PROPOSTA_COMERCIAL",
    label: "Proposta Comercial",
  },
  {
    value: "CONTRATO",
    label: "Contrato",
  },
];

const STATUS_ASSINANTE = [
  {
    value: "ATIVO",
    label: "Ativo",
  },
  {
    value: "INATIVO",
    label: "Inativo",
  },
];

const formVazio = {
  nome: "",
  processo: "",
};

const proximaOrdemSugerida = (lista = []) => {
  if (!lista.length) {
    return 1;
  }

  return Math.max(...lista.map((assinante) => Number(assinante.ordem) || 0)) + 1;
};

const novoAssinanteVazio = (lista = []) => ({
  usuarioId: null,
  ordem: String(proximaOrdemSugerida(lista)),
  email: "",
  status: "ATIVO",
});

const labelProcesso = (valor) => PROCESSOS.find((processo) => processo.value === valor)?.label ?? valor ?? "—";

const labelStatus = (valor) => STATUS_ASSINANTE.find((status) => status.value === valor)?.label ?? valor ?? "—";

const normalizarTexto = (valor) =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export default function AlcadasAssinaturas() {
  /*
   * Nesta primeira etapa as alçadas ficam somente no estado da tela.
   *
   * Não existe POST, PUT ou DELETE no backend.
   *
   * A persistência será implementada posteriormente, quando criarmos
   * a estrutura própria de backend/banco para Alçadas de Assinaturas.
   */
  const [alcadas, setAlcadas] = useState([]);

  /*
   * Utilizamos somente a consulta existente de usuários.
   * Nenhum cadastro de Alçada de Aprovação é alterado.
   */
  const [usuarios, setUsuarios] = useState([]);
  const [carregandoUsuarios, setCarregandoUsuarios] = useState(false);

  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");

  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(5);

  const [gridMinimizado, setGridMinimizado] = useState(false);

  const nomeInputRef = useRef(null);

  /*
   * null = cadastro novo
   * outro valor = alçada selecionada
   */
  const [editandoId, setEditandoId] = useState(null);
  const [somenteLeitura, setSomenteLeitura] = useState(false);

  const [form, setForm] = useState({
    ...formVazio,
  });

  const [formAssinantes, setFormAssinantes] = useState([]);

  const [novoAssinante, setNovoAssinante] = useState(novoAssinanteVazio([]));

  /*
   * Guarda o usuarioId ORIGINAL da linha que está sendo editada.
   */
  const [editAssinanteUid, setEditAssinanteUid] = useState(null);

  const [editAssinanteForm, setEditAssinanteForm] = useState({
    usuarioId: null,
    ordem: "",
    email: "",
    status: "ATIVO",
  });

  const [confirmDelete, setConfirmDelete] = useState({
    aberto: false,
    tipo: null,
    alcadaId: null,
    uid: null,
  });

  const [errosForm, setErrosForm] = useState({});

  const [snack, setSnack] = useState({
    aberto: false,
    msg: "",
    severity: "success",
  });

  const mostrarSnack = (msg, severity = "success") => {
    setSnack({
      aberto: true,
      msg,
      severity,
    });
  };

  /*
   * ============================================================
   * PERMISSÕES
   * ============================================================
   */

  const token = localStorage.getItem("token");

  let tipoUsuario = "";

  if (token) {
    try {
      const decoded = jwtDecode(token);

      tipoUsuario = decoded.tipo
        ?.toString()
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    } catch (_) {
      tipoUsuario = "";
    }
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);

  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);

  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  /*
   * ============================================================
   * ALÇADAS DE ASSINATURAS
   * ============================================================
   */

  const carregarAlcadas = useCallback(async () => {
    const tk = localStorage.getItem("token");

    if (!tk) {
      setAlcadas([]);
      return;
    }

    try {
      const params = new URLSearchParams({
        filtro: "",
        pagina: "0",
        porPagina: "100",
      });

      const response = await fetch(`/api/alcadas-assinaturas?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${tk}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao buscar alçadas de assinaturas.");
      }

      setAlcadas(Array.isArray(data?.data) ? data.data : []);
    } catch (erro) {
      console.error("Erro ao buscar Alçadas de Assinaturas:", erro);

      setSnack({
        aberto: true,
        msg: erro.message || "Não foi possível carregar as Alçadas de Assinaturas.",
        severity: "error",
      });
    }
  }, []);

  useEffect(() => {
    if (!podeVisualizar) {
      return;
    }

    carregarAlcadas();
  }, [podeVisualizar, carregarAlcadas]);

  /*
   * ============================================================
   * USUÁRIOS
   * ============================================================
   */

  useEffect(() => {
    const tk = localStorage.getItem("token");

    if (!tk) {
      return;
    }

    setCarregandoUsuarios(true);

    fetch("/api/alcadas/usuarios", {
      headers: {
        Authorization: `Bearer ${tk}`,
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error("Erro ao buscar usuários.");
        }

        return res.json();
      })
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

        setUsuarios(lista);
      })
      .catch((erro) => {
        console.error("Erro ao buscar usuários para Alçada de Assinaturas:", erro);

        mostrarSnack("Não foi possível carregar a lista de usuários.", "error");
      })
      .finally(() => {
        setCarregandoUsuarios(false);
      });
  }, []);

  const localizarUsuario = (usuarioId) => usuarios.find((usuario) => String(usuario.id) === String(usuarioId));

  const obterNomeUsuario = (assinante) => assinante?.usuario?.nome ?? localizarUsuario(assinante?.usuarioId)?.nome ?? "—";

  const obterEmailUsuario = (assinante) => assinante?.email || assinante?.usuario?.email || localizarUsuario(assinante?.usuarioId)?.email || "—";

  /*
   * ============================================================
   * FILTRO
   * ============================================================
   */

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFiltro(inputFiltro);
      setPagina(0);
    }, 400);

    return () => clearTimeout(timeout);
  }, [inputFiltro]);

  const handlePesquisar = () => {
    setFiltro(inputFiltro);
    setPagina(0);
  };

  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPagina(0);
  };

  const alcadasFiltradas = useMemo(() => {
    const termo = normalizarTexto(filtro);

    if (!termo) {
      return alcadas;
    }

    return alcadas.filter((alcada) => {
      const dadosAlcada = [alcada.nome, labelProcesso(alcada.processo)];

      const encontrouNaAlcada = dadosAlcada.some((valor) => normalizarTexto(valor).includes(termo));

      if (encontrouNaAlcada) {
        return true;
      }

      return (alcada.assinantes ?? []).some((assinante) => {
        const dadosAssinante = [obterNomeUsuario(assinante), obterEmailUsuario(assinante), labelStatus(assinante.status), assinante.ordem];

        return dadosAssinante.some((valor) => normalizarTexto(valor).includes(termo));
      });
    });
  }, [alcadas, filtro, usuarios]);

  const total = alcadasFiltradas.length;

  const alcadasPaginadas = useMemo(() => {
    const inicio = pagina * porPagina;
    const fim = inicio + porPagina;

    return alcadasFiltradas.slice(inicio, fim);
  }, [alcadasFiltradas, pagina, porPagina]);

  useEffect(() => {
    if (pagina > 0 && pagina * porPagina >= total) {
      setPagina(0);
    }
  }, [pagina, porPagina, total]);

  /*
   * ============================================================
   * COPIAR
   * ============================================================
   */

  const handleCopiar = async () => {
    const texto = alcadasFiltradas
      .map((alcada) => {
        const assinantes = (alcada.assinantes ?? [])
          .slice()
          .sort((a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0))
          .map((assinante) => `${assinante.ordem}º - ${obterNomeUsuario(assinante)} - ${obterEmailUsuario(assinante)} - ${labelStatus(assinante.status)}`)
          .join(" | ");

        return `${labelProcesso(alcada.processo)} - ${alcada.nome} - ${assinantes}`;
      })
      .join("\n");

    if (!texto.trim()) {
      mostrarSnack("Nada para copiar.", "warning");
      return;
    }

    const ok = await copiarTexto(texto);

    mostrarSnack(ok ? "Copiado!" : "Não foi possível copiar.", ok ? "success" : "error");
  };

  /*
   * ============================================================
   * CSV
   * ============================================================
   */

  const handleCSV = () => {
    const BOM = "\uFEFF";

    const header = ["Processo", "Nome da Alçada", "Ordem", "Usuário", "E-mail", "Status"];

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const linhas = [];

    alcadasFiltradas.forEach((alcada) => {
      const assinantes = (alcada.assinantes ?? []).slice().sort((a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0));

      if (!assinantes.length) {
        linhas.push([labelProcesso(alcada.processo), alcada.nome || "", "", "", "", ""]);

        return;
      }

      assinantes.forEach((assinante) => {
        linhas.push([
          labelProcesso(alcada.processo),
          alcada.nome || "",
          assinante.ordem ?? "",
          obterNomeUsuario(assinante),
          obterEmailUsuario(assinante),
          labelStatus(assinante.status),
        ]);
      });
    });

    const csv = [header, ...linhas].map((linha) => linha.map(csvEscape).join(";")).join("\n");

    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", "alcadas-assinaturas.csv");

    document.body.appendChild(link);

    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };

  /*
   * ============================================================
   * VISUALIZAR / EDITAR ALÇADA
   * ============================================================
   */

  const handleVisualizar = (alcada) => {
    setEditandoId(alcada.id);
    setSomenteLeitura(true);

    setForm({
      nome: alcada.nome ?? "",
      processo: alcada.processo ?? "",
    });

    setFormAssinantes(
      (alcada.assinantes ?? []).map((assinante) => ({
        ...assinante,
      })),
    );

    setNovoAssinante(novoAssinanteVazio(alcada.assinantes ?? []));

    setEditAssinanteUid(null);
    setErrosForm({});
    setGridMinimizado(true);

    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "smooth",
    });

    setTimeout(() => {
      nomeInputRef.current?.focus();
    }, 300);
  };

  const handleHabilitarEdicao = () => {
    setSomenteLeitura(false);

    setTimeout(() => {
      nomeInputRef.current?.focus();
    }, 50);
  };

  const handleCancelarForm = () => {
    setEditandoId(null);
    setSomenteLeitura(false);

    setForm({
      ...formVazio,
    });

    setFormAssinantes([]);

    setNovoAssinante(novoAssinanteVazio([]));

    setEditAssinanteUid(null);

    setEditAssinanteForm({
      usuarioId: null,
      ordem: "",
      email: "",
      status: "ATIVO",
    });

    setErrosForm({});
    setGridMinimizado(false);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /*
   * ============================================================
   * SALVAR ALÇADA
   * ============================================================
   */

  const handleSalvar = async () => {
    const novosErros = {};

    if (!form.nome.trim()) {
      novosErros.nome = "Nome é obrigatório.";
    }

    if (!form.processo) {
      novosErros.processo = "Processo é obrigatório.";
    }

    if (Object.keys(novosErros).length > 0) {
      setErrosForm(novosErros);

      nomeInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      return;
    }

    if (novoAssinante.usuarioId) {
      mostrarSnack("Há um assinante pendente de adição. Clique em Adicionar ou limpe os campos antes de salvar.", "warning");

      return;
    }

    if (editAssinanteUid !== null) {
      mostrarSnack("Há uma edição de assinante em andamento. Confirme ou cancele antes de salvar.", "warning");

      return;
    }

    if (formAssinantes.length === 0) {
      mostrarSnack("Adicione pelo menos um assinante à alçada.", "warning");

      return;
    }

    const assinantes = formAssinantes
      .map((assinante) => ({
        usuarioId: Number(assinante.usuarioId),
        ordem: Number(assinante.ordem),
        email: assinante.email?.trim() ?? "",
        status: assinante.status ?? "ATIVO",
      }))
      .sort((a, b) => a.ordem - b.ordem);

    const tk = localStorage.getItem("token");

    if (!tk) {
      mostrarSnack("Sessão inválida. Faça login novamente.", "error");
      return;
    }

    const estaEditando = editandoId !== null;

    const url = estaEditando ? `/api/alcadas-assinaturas/${editandoId}` : "/api/alcadas-assinaturas";

    const method = estaEditando ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tk}`,
        },
        body: JSON.stringify({
          nome: form.nome.trim(),
          processo: form.processo,
          assinantes,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        mostrarSnack(data.erro || (estaEditando ? "Erro ao atualizar a Alçada de Assinaturas." : "Erro ao cadastrar a Alçada de Assinaturas."), "error");

        return;
      }

      mostrarSnack(estaEditando ? "Alçada de Assinaturas atualizada com sucesso!" : "Alçada de Assinaturas cadastrada com sucesso!", "success");

      handleCancelarForm();

      await carregarAlcadas();
    } catch (erro) {
      console.error("Erro ao salvar Alçada de Assinaturas:", erro);

      mostrarSnack("Erro de conexão ao salvar a Alçada de Assinaturas.", "error");
    }
  };
  /*
   * ============================================================
   * EXCLUIR ALÇADA
   * ============================================================
   */

  const handleExcluirAlcada = async () => {
    const { alcadaId } = confirmDelete;

    setConfirmDelete({
      aberto: false,
      tipo: null,
      alcadaId: null,
      uid: null,
    });

    const tk = localStorage.getItem("token");

    if (!tk) {
      mostrarSnack("Sessão inválida. Faça login novamente.", "error");
      return;
    }

    try {
      const response = await fetch(`/api/alcadas-assinaturas/${alcadaId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${tk}`,
        },
      });

      if (!response.ok && response.status !== 204) {
        const data = await response.json().catch(() => ({}));

        mostrarSnack(data.erro || "Erro ao excluir a Alçada de Assinaturas.", "error");

        return;
      }

      if (editandoId === alcadaId) {
        handleCancelarForm();
      }

      mostrarSnack("Alçada de Assinaturas excluída com sucesso.", "warning");

      await carregarAlcadas();
    } catch (erro) {
      console.error("Erro ao excluir Alçada de Assinaturas:", erro);

      mostrarSnack("Erro de conexão ao excluir a Alçada de Assinaturas.", "error");
    }
  };

  /*
   * ============================================================
   * ADICIONAR ASSINANTE
   * ============================================================
   */

  const handleAdicionarAssinante = () => {
    if (!novoAssinante.usuarioId) {
      mostrarSnack("Selecione um usuário.", "warning");
      return;
    }

    if (!novoAssinante.ordem) {
      mostrarSnack("Ordem é obrigatória.", "warning");
      return;
    }

    const ordemNum = Number(novoAssinante.ordem);

    if (!Number.isInteger(ordemNum) || ordemNum <= 0) {
      mostrarSnack("Ordem deve ser um número inteiro positivo.", "warning");
      return;
    }

    if (!novoAssinante.email.trim()) {
      mostrarSnack("Informe o e-mail do assinante.", "warning");
      return;
    }

    if (!novoAssinante.status) {
      mostrarSnack("Informe o status do assinante.", "warning");
      return;
    }

    if (formAssinantes.some((assinante) => Number(assinante.ordem) === ordemNum)) {
      mostrarSnack(`Ordem ${ordemNum} já está em uso por outro assinante.`, "warning");
      return;
    }

    if (formAssinantes.some((assinante) => String(assinante.usuarioId) === String(novoAssinante.usuarioId))) {
      mostrarSnack("Usuário já está na lista de assinantes.", "warning");

      return;
    }

    const usuarioObj = localizarUsuario(novoAssinante.usuarioId);

    const atualizados = [
      ...formAssinantes,
      {
        id: null,
        usuarioId: novoAssinante.usuarioId,
        ordem: ordemNum,
        email: novoAssinante.email.trim(),
        status: novoAssinante.status,
        usuario: usuarioObj,
      },
    ].sort((a, b) => Number(a.ordem) - Number(b.ordem));

    setFormAssinantes(atualizados);

    setNovoAssinante(novoAssinanteVazio(atualizados));
  };

  /*
   * ============================================================
   * EDITAR ASSINANTE
   * ============================================================
   */

  const handleSalvarEditAssinante = () => {
    if (!editAssinanteForm.usuarioId) {
      mostrarSnack("Selecione um usuário.", "warning");

      return;
    }

    if (!editAssinanteForm.ordem) {
      mostrarSnack("Ordem é obrigatória.", "warning");

      return;
    }

    const ordemNum = Number(editAssinanteForm.ordem);

    if (!Number.isInteger(ordemNum) || ordemNum <= 0) {
      mostrarSnack("Ordem deve ser um número inteiro positivo.", "warning");

      return;
    }

    if (!editAssinanteForm.email.trim()) {
      mostrarSnack("Informe o e-mail do assinante.", "warning");
      return;
    }

    if (
      formAssinantes.some(
        (assinante) => String(assinante.usuarioId) !== String(editAssinanteUid) && String(assinante.usuarioId) === String(editAssinanteForm.usuarioId),
      )
    ) {
      mostrarSnack("Usuário já está na lista de assinantes.", "warning");

      return;
    }

    if (formAssinantes.some((assinante) => String(assinante.usuarioId) !== String(editAssinanteUid) && Number(assinante.ordem) === ordemNum)) {
      mostrarSnack(`Ordem ${ordemNum} já está em uso por outro assinante.`, "warning");

      return;
    }

    const usuarioObj = localizarUsuario(editAssinanteForm.usuarioId);

    setFormAssinantes((anteriores) =>
      anteriores
        .map((assinante) =>
          String(assinante.usuarioId) === String(editAssinanteUid)
            ? {
                ...assinante,
                usuarioId: editAssinanteForm.usuarioId,
                usuario: usuarioObj,
                ordem: ordemNum,
                email: editAssinanteForm.email.trim(),
                status: editAssinanteForm.status,
              }
            : assinante,
        )
        .sort((a, b) => Number(a.ordem) - Number(b.ordem)),
    );

    setEditAssinanteUid(null);

    setEditAssinanteForm({
      usuarioId: null,
      ordem: "",
      email: "",
      status: "ATIVO",
    });
  };

  /*
   * ============================================================
   * EXCLUIR ASSINANTE
   * ============================================================
   */

  const handleExcluirVinculo = () => {
    const { uid } = confirmDelete;

    const restantes = formAssinantes.filter((assinante) => String(assinante.usuarioId) !== String(uid));

    setFormAssinantes(restantes);

    setNovoAssinante(novoAssinanteVazio(restantes));

    setConfirmDelete({
      aberto: false,
      tipo: null,
      alcadaId: null,
      uid: null,
    });
  };

  const handleConfirmarDelete = (tipo, alcadaId, uid = null) => {
    document.activeElement?.blur();

    setConfirmDelete({
      aberto: true,
      tipo,
      alcadaId,
      uid,
    });
  };

  const handleExcluirConfirmado = () => {
    if (confirmDelete.tipo === "alcada") {
      handleExcluirAlcada();
      return;
    }

    handleExcluirVinculo();
  };

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        <Typography
          variant="h5"
          sx={{
            mb: 2,
            fontWeight: 600,
          }}
        >
          Alçadas de Assinaturas
        </Typography>

        {/* =====================================================
            FILTRO
        ===================================================== */}

        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 1,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Filtrar Alçada"
            value={inputFiltro}
            onChange={(e) => setInputFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handlePesquisar();
              }
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      sx={{
                        color: "var(--ig-t-9ca3af)",
                        fontSize: 20,
                      }}
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,

              "& .MuiOutlinedInput-root": {
                backgroundColor: "transparent",
                color: "var(--ig-t-ffffff)",
              },

              "& input::placeholder": {
                color: "var(--ig-t-ffffff)",
                opacity: 1,
                fontWeight: 500,
                fontSize: "1rem",
              },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleLimparFiltro}
            sx={{
              flexShrink: 0,
              width: "auto",
              height: 40,
              color: "#2196f3",
              borderColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",

              "&:hover": {
                borderColor: "#42a5f5",
                backgroundColor: "rgba(33, 150, 243, 0.08)",
              },
            }}
          >
            Limpar Filtro
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCopiar}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",

              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            <i
              className="fas fa-copy"
              style={{
                marginRight: 6,
              }}
            />
            Copiar
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCSV}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",

              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            CSV
          </Button>
        </Box>

        {/* =====================================================
            MINIMIZAR / EXPANDIR GRID
        ===================================================== */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 1,
          }}
        >
          {!gridMinimizado ? (
            <button
              type="button"
              onClick={() => setGridMinimizado(true)}
              style={{
                border: "1px solid #d32f2f",
                color: "#ff4c4c",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setGridMinimizado(false)}
              style={{
                border: "1px solid #1998a8",
                color: "#1998a8",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↗ Expandir
            </button>
          )}
        </Box>

        {/* =====================================================
            GRID DAS ALÇADAS
        ===================================================== */}

        <Collapse in={!gridMinimizado}>
          <TableContainer
            component={Paper}
            sx={{
              mb: 3,
              backgroundColor: "transparent",
              boxShadow: "none",
            }}
          >
            <Table
              size="small"
              sx={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.95rem",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "var(--ig-f-1e1e1e)",
                      color: "var(--ig-t-ffffff)",
                      fontWeight: "bold",
                      borderBottom: "1px solid var(--ig-b-444444)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Ações
                  </TableCell>

                  <TableCell
                    sx={{
                      backgroundColor: "var(--ig-f-1e1e1e)",
                      color: "var(--ig-t-ffffff)",
                      fontWeight: "bold",
                      borderBottom: "1px solid var(--ig-b-444444)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Processo
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "var(--ig-f-1e1e1e)",
                      color: "var(--ig-t-ffffff)",
                      fontWeight: "bold",
                      borderBottom: "1px solid var(--ig-b-444444)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Assinantes
                  </TableCell>

                  <TableCell
                    sx={{
                      backgroundColor: "var(--ig-f-1e1e1e)",
                      color: "var(--ig-t-ffffff)",
                      fontWeight: "bold",
                      borderBottom: "1px solid var(--ig-b-444444)",
                      width: "100%",
                    }}
                  >
                    Nome
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {alcadasPaginadas.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{
                        color: "var(--ig-t-888888)",
                        py: 3,
                        borderBottom: "1px solid var(--ig-b-444444)",
                      }}
                    >
                      Nenhuma alçada de assinaturas encontrada.
                    </TableCell>
                  </TableRow>
                ) : (
                  alcadasPaginadas.map((alcada) => (
                    <TableRow
                      key={alcada.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "var(--ig-f-2a2a2a)",
                        },

                        backgroundColor: editandoId === alcada.id ? "rgba(25,152,168,0.08)" : "transparent",
                      }}
                    >
                      <TableCell
                        sx={{
                          borderBottom: "1px solid var(--ig-b-444444)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {podeVisualizar && (
                          <Tooltip title="Visualizar / Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleVisualizar(alcada)}
                              sx={{
                                backgroundColor: "#1998a8",
                                color: "#fff",
                                borderRadius: "4px",
                                mx: 0.25,
                                width: "auto",

                                "&:hover": {
                                  backgroundColor: "#1565c0",
                                },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {podeExcluir && (
                          <Tooltip title="Excluir alçada">
                            <IconButton
                              size="small"
                              onClick={() => handleConfirmarDelete("alcada", alcada.id)}
                              sx={{
                                backgroundColor: "#d32f2f",
                                color: "#fff",
                                borderRadius: "4px",
                                mx: 0.25,
                                width: "auto",

                                "&:hover": {
                                  backgroundColor: "#c62828",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>

                      <TableCell
                        sx={{
                          borderBottom: "1px solid var(--ig-b-444444)",
                          color: "var(--ig-t-ffffff)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {labelProcesso(alcada.processo)}
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          borderBottom: "1px solid var(--ig-b-444444)",
                          color: "var(--ig-t-aaaaaa)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          <PeopleAltIcon fontSize="small" />

                          <span>{alcada.assinantes?.length ?? 0}</span>
                        </Box>
                      </TableCell>

                      <TableCell
                        sx={{
                          borderBottom: "1px solid var(--ig-b-444444)",
                          color: "var(--ig-t-ffffff)",
                          width: "100%",
                        }}
                      >
                        {alcada.nome}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={total}
              page={pagina}
              onPageChange={(_, novaPagina) => setPagina(novaPagina)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(e) => {
                setPorPagina(parseInt(e.target.value, 10));

                setPagina(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Alçadas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                color: "var(--ig-t-ffffff)",
                borderTop: "1px solid var(--ig-b-444444)",

                "& .MuiTablePagination-selectIcon": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-actions button": {
                  color: "var(--ig-t-ffffff)",
                  width: "auto",
                },

                "& .MuiTablePagination-actions": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          </TableContainer>
        </Collapse>

        {/* =====================================================
            FORMULÁRIO
        ===================================================== */}

        {(podeCadastrar || (podeVisualizar && editandoId)) && (
          <Paper
            sx={{
              p: 3,
              backgroundColor: "var(--ig-f-1e1e1e)",
              borderRadius: 2,
              boxShadow: "0 0 20px rgba(0,0,0,0.4)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: "var(--ig-t-ffffff)",
                fontWeight: 600,
              }}
            >
              {editandoId && somenteLeitura ? "Visualizar Alçada de Assinaturas" : editandoId ? "Editar Alçada de Assinaturas" : "Nova Alçada de Assinaturas"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {/* NOME */}

              <TextField
                label="Nome da Alçada*"
                size="small"
                fullWidth
                inputRef={nomeInputRef}
                value={form.nome}
                onChange={(e) => {
                  if (!somenteLeitura) {
                    setForm((anterior) => ({
                      ...anterior,
                      nome: e.target.value.toUpperCase(),
                    }));

                    if (errosForm.nome) {
                      setErrosForm((anterior) => ({
                        ...anterior,
                        nome: undefined,
                      }));
                    }
                  }
                }}
                onBlur={() => {
                  if (!somenteLeitura && !form.nome.trim()) {
                    setErrosForm((anterior) => ({
                      ...anterior,
                      nome: "Nome é obrigatório.",
                    }));
                  }
                }}
                error={!!errosForm.nome}
                helperText={errosForm.nome}
                slotProps={{
                  input: {
                    readOnly: somenteLeitura,
                  },

                  htmlInput: {
                    maxLength: 200,
                  },
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: errosForm.nome ? "#f44336" : "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                  },

                  "& .MuiOutlinedInput-root": {
                    backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",

                    color: "var(--ig-t-ffffff)",

                    "& fieldset": {
                      borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.nome ? "#f44336" : "var(--ig-b-444444)",
                    },

                    "&:hover fieldset": {
                      borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.nome ? "#f44336" : "var(--ig-b-888888)",
                    },
                  },

                  "& input": {
                    color: "var(--ig-t-ffffff)",
                  },

                  "& .MuiFormHelperText-root": {
                    color: "#f44336",
                  },
                }}
              />

              {/* PROCESSO */}

              <FormControl size="small" fullWidth error={!!errosForm.processo}>
                <InputLabel
                  sx={{
                    color: errosForm.processo ? "#f44336" : "var(--ig-t-ffffff)",

                    fontWeight: "bold",
                  }}
                >
                  Processo*
                </InputLabel>

                <Select
                  label="Processo*"
                  value={form.processo}
                  onChange={(e) => {
                    if (!somenteLeitura) {
                      setForm((anterior) => ({
                        ...anterior,
                        processo: e.target.value,
                      }));

                      if (errosForm.processo) {
                        setErrosForm((anterior) => ({
                          ...anterior,
                          processo: undefined,
                        }));
                      }
                    }
                  }}
                  onBlur={() => {
                    if (!somenteLeitura && !form.processo) {
                      setErrosForm((anterior) => ({
                        ...anterior,
                        processo: "Processo é obrigatório.",
                      }));
                    }
                  }}
                  inputProps={{
                    readOnly: somenteLeitura,
                  }}
                  sx={{
                    backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",

                    color: "var(--ig-t-ffffff)",

                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.processo ? "#f44336" : "var(--ig-b-444444)",
                    },

                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.processo ? "#f44336" : "var(--ig-b-888888)",
                    },

                    "& .MuiSvgIcon-root": {
                      color: somenteLeitura ? "transparent" : "var(--ig-t-ffffff)",
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>

                  {PROCESSOS.map((processo) => (
                    <MenuItem key={processo.value} value={processo.value}>
                      {processo.label}
                    </MenuItem>
                  ))}
                </Select>

                {errosForm.processo && (
                  <FormHelperText
                    sx={{
                      color: "#f44336",
                    }}
                  >
                    {errosForm.processo}
                  </FormHelperText>
                )}
              </FormControl>

              {/* =================================================
                  ASSINANTES
              ================================================= */}

              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "var(--ig-t-cccccc)",
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Assinantes
                </Typography>

                <Table
                  size="small"
                  sx={{
                    width: "100%",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "var(--ig-f-252525)",
                          color: "var(--ig-t-aaaaaa)",
                          fontWeight: "bold",
                          borderBottom: "1px solid var(--ig-b-444444)",
                          py: 0.8,
                          whiteSpace: "nowrap",
                          width: "80px",
                        }}
                      >
                        Ordem
                      </TableCell>

                      <TableCell
                        sx={{
                          backgroundColor: "var(--ig-f-252525)",
                          color: "var(--ig-t-aaaaaa)",
                          fontWeight: "bold",
                          borderBottom: "1px solid var(--ig-b-444444)",
                          py: 0.8,
                          minWidth: 220,
                        }}
                      >
                        Usuário
                      </TableCell>

                      <TableCell
                        sx={{
                          backgroundColor: "var(--ig-f-252525)",
                          color: "var(--ig-t-aaaaaa)",
                          fontWeight: "bold",
                          borderBottom: "1px solid var(--ig-b-444444)",
                          py: 0.8,
                          minWidth: 220,
                        }}
                      >
                        E-mail
                      </TableCell>

                      <TableCell
                        sx={{
                          backgroundColor: "var(--ig-f-252525)",
                          color: "var(--ig-t-aaaaaa)",
                          fontWeight: "bold",
                          borderBottom: "1px solid var(--ig-b-444444)",
                          py: 0.8,
                          width: "130px",
                        }}
                      >
                        Status
                      </TableCell>

                      {!somenteLeitura && podeCadastrar && (
                        <TableCell
                          align="right"
                          sx={{
                            backgroundColor: "var(--ig-f-252525)",
                            color: "var(--ig-t-aaaaaa)",
                            fontWeight: "bold",
                            borderBottom: "1px solid var(--ig-b-444444)",
                            py: 0.8,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Ações
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {/* SEM ASSINANTES */}

                    {formAssinantes.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={!somenteLeitura && podeCadastrar ? 5 : 4}
                          sx={{
                            borderBottom: "1px solid var(--ig-b-333333)",
                            color: "var(--ig-t-666666)",
                            fontStyle: "italic",
                            py: 1.5,
                            textAlign: "center",
                          }}
                        >
                          Nenhum assinante adicionado.
                        </TableCell>
                      </TableRow>
                    )}

                    {/* ASSINANTES JÁ ADICIONADOS */}

                    {formAssinantes.map((assinante) =>
                      String(editAssinanteUid) === String(assinante.usuarioId) ? (
                        /*
                         * =====================================
                         * LINHA DE EDIÇÃO
                         * =====================================
                         */
                        <TableRow
                          key={assinante.usuarioId}
                          sx={{
                            backgroundColor: "rgba(25,152,168,0.08)",
                          }}
                        >
                          <TableCell
                            sx={{
                              borderBottom: "1px solid var(--ig-b-333333)",
                              py: 0.5,
                            }}
                          >
                            <TextField
                              size="small"
                              type="number"
                              value={editAssinanteForm.ordem}
                              onChange={(e) =>
                                setEditAssinanteForm((anterior) => ({
                                  ...anterior,
                                  ordem: e.target.value,
                                }))
                              }
                              slotProps={{
                                htmlInput: {
                                  min: 1,
                                },
                              }}
                              sx={{
                                minWidth: 75,

                                "& .MuiOutlinedInput-root": {
                                  color: "var(--ig-t-ffffff)",
                                  backgroundColor: "var(--ig-f-2a2a2a)",

                                  "& fieldset": {
                                    borderColor: "var(--ig-b-444444)",
                                  },
                                },

                                "& input": {
                                  color: "var(--ig-t-ffffff)",
                                },
                              }}
                            />
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: "1px solid var(--ig-b-333333)",
                              py: 0.5,
                            }}
                          >
                            <Autocomplete
                              size="small"
                              disableClearable
                              loading={carregandoUsuarios}
                              options={usuarios.filter(
                                (usuario) =>
                                  String(usuario.id) === String(editAssinanteUid) ||
                                  !formAssinantes.some((item) => String(item.usuarioId) === String(usuario.id)),
                              )}
                              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                              getOptionLabel={(usuario) => usuario.nome ?? ""}
                              value={localizarUsuario(editAssinanteForm.usuarioId) || null}
                              onChange={(_, valor) =>
                                setEditAssinanteForm((anterior) => ({
                                  ...anterior,
                                  usuarioId: valor?.id ?? null,
                                  email: valor?.email ?? "",
                                }))
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Selecionar usuário"
                                  size="small"
                                  sx={{
                                    minWidth: 220,

                                    "& .MuiOutlinedInput-root": {
                                      color: "var(--ig-t-ffffff)",
                                      backgroundColor: "var(--ig-f-2a2a2a)",

                                      "& fieldset": {
                                        borderColor: "var(--ig-b-444444)",
                                      },
                                    },

                                    "& input": {
                                      color: "var(--ig-t-ffffff)",
                                    },
                                  }}
                                />
                              )}
                              sx={{
                                minWidth: 220,
                              }}
                            />
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: "1px solid var(--ig-b-333333)",
                              py: 0.5,
                            }}
                          >
                            <TextField
                              size="small"
                              fullWidth
                              type="email"
                              value={editAssinanteForm.email}
                              placeholder="E-mail do assinante"
                              onChange={(e) =>
                                setEditAssinanteForm((anterior) => ({
                                  ...anterior,
                                  email: e.target.value,
                                }))
                              }
                              sx={{
                                minWidth: 220,

                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "var(--ig-f-2a2a2a)",
                                  color: "var(--ig-t-ffffff)",

                                  "& fieldset": {
                                    borderColor: "var(--ig-b-444444)",
                                  },
                                },

                                "& input": {
                                  color: "var(--ig-t-ffffff)",
                                },
                              }}
                            />
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: "1px solid var(--ig-b-333333)",
                              py: 0.5,
                            }}
                          >
                            <FormControl size="small" fullWidth>
                              <Select
                                value={editAssinanteForm.status}
                                onChange={(e) =>
                                  setEditAssinanteForm((anterior) => ({
                                    ...anterior,
                                    status: e.target.value,
                                  }))
                                }
                                sx={{
                                  color: "var(--ig-t-ffffff)",
                                  backgroundColor: "var(--ig-f-2a2a2a)",

                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "var(--ig-b-444444)",
                                  },

                                  "& .MuiSvgIcon-root": {
                                    color: "var(--ig-t-ffffff)",
                                  },
                                }}
                              >
                                {STATUS_ASSINANTE.map((status) => (
                                  <MenuItem key={status.value} value={status.value}>
                                    {status.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              borderBottom: "1px solid var(--ig-b-333333)",
                              py: 0.5,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Tooltip title="Confirmar edição">
                              <IconButton
                                size="small"
                                onClick={handleSalvarEditAssinante}
                                sx={{
                                  backgroundColor: "#2e7d32",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",

                                  "&:hover": {
                                    backgroundColor: "#1b5e20",
                                  },
                                }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Cancelar edição">
                              <IconButton
                                size="small"
                                onClick={() => setEditAssinanteUid(null)}
                                sx={{
                                  backgroundColor: "#555",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",

                                  "&:hover": {
                                    backgroundColor: "#444",
                                  },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ) : (
                        /*
                         * =====================================
                         * LINHA NORMAL
                         * =====================================
                         */
                        <TableRow
                          key={assinante.usuarioId}
                          sx={{
                            "&:hover": {
                              backgroundColor: "var(--ig-f-2a2a2a)",
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              borderBottom: "1px solid var(--ig-b-333333)",
                              color: "var(--ig-t-ffffff)",
                              py: 0.8,
                            }}
                          >
                            {assinante.ordem}
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: "1px solid var(--ig-b-333333)",
                              color: "var(--ig-t-ffffff)",
                              py: 0.8,
                            }}
                          >
                            {obterNomeUsuario(assinante)}
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: "1px solid var(--ig-b-333333)",
                              color: "var(--ig-t-cccccc)",
                              py: 0.8,
                            }}
                          >
                            {obterEmailUsuario(assinante)}
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: "1px solid var(--ig-b-333333)",
                              color: assinante.status === "ATIVO" ? "#66bb6a" : "var(--ig-t-aaaaaa)",
                              py: 0.8,
                              fontWeight: 600,
                            }}
                          >
                            {labelStatus(assinante.status)}
                          </TableCell>

                          {!somenteLeitura && podeCadastrar && (
                            <TableCell
                              align="right"
                              sx={{
                                borderBottom: "1px solid var(--ig-b-333333)",
                                py: 0.8,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Tooltip
                                title={editAssinanteUid !== null ? "Confirme ou cancele a edição em andamento antes de editar outro" : "Editar assinante"}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={editAssinanteUid !== null}
                                    onClick={() => {
                                      setEditAssinanteUid(assinante.usuarioId);

                                      setEditAssinanteForm({
                                        usuarioId: assinante.usuarioId,
                                        ordem: String(assinante.ordem ?? ""),
                                        email: obterEmailUsuario(assinante) === "—" ? "" : obterEmailUsuario(assinante),
                                        status: assinante.status ?? "ATIVO",
                                      });
                                    }}
                                    sx={{
                                      backgroundColor: editAssinanteUid !== null ? "var(--ig-f-333333)" : "#1998a8",

                                      color: "#fff",
                                      borderRadius: "4px",
                                      mx: 0.25,
                                      width: "auto",

                                      "&:hover": {
                                        backgroundColor: editAssinanteUid !== null ? "var(--ig-f-333333)" : "#1565c0",
                                      },

                                      "&.Mui-disabled": {
                                        color: "var(--ig-t-666666)",
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {!assinante.id && (
                                <Tooltip title="Remover assinante">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleConfirmarDelete("vinculo", editandoId, assinante.usuarioId)}
                                    sx={{
                                      backgroundColor: "#d32f2f",
                                      color: "#fff",
                                      borderRadius: "4px",
                                      mx: 0.25,
                                      width: "auto",

                                      "&:hover": {
                                        backgroundColor: "#c62828",
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ),
                    )}

                    {/* =========================================
                        LINHA PARA NOVO ASSINANTE
                    ========================================= */}

                    {!somenteLeitura && podeCadastrar && (
                      <TableRow
                        sx={{
                          backgroundColor: "var(--ig-f-1a1a1a)",
                        }}
                      >
                        <TableCell
                          sx={{
                            borderBottom: "1px solid var(--ig-b-333333)",
                            py: 0.5,
                          }}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={novoAssinante.ordem}
                            onChange={(e) =>
                              setNovoAssinante((anterior) => ({
                                ...anterior,
                                ordem: e.target.value,
                              }))
                            }
                            placeholder="Nº"
                            slotProps={{
                              htmlInput: {
                                min: 1,
                              },
                            }}
                            sx={{
                              minWidth: 75,

                              "& .MuiOutlinedInput-root": {
                                color: "var(--ig-t-ffffff)",
                                backgroundColor: "var(--ig-f-2a2a2a)",

                                "& fieldset": {
                                  borderColor: "var(--ig-b-444444)",
                                },
                              },

                              "& input": {
                                color: "var(--ig-t-ffffff)",
                              },
                            }}
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            borderBottom: "1px solid var(--ig-b-333333)",
                            py: 0.5,
                          }}
                        >
                          <Autocomplete
                            size="small"
                            loading={carregandoUsuarios}
                            options={usuarios.filter((usuario) => !formAssinantes.some((assinante) => String(assinante.usuarioId) === String(usuario.id)))}
                            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                            getOptionLabel={(usuario) => usuario.nome ?? ""}
                            value={localizarUsuario(novoAssinante.usuarioId) || null}
                            onChange={(_, valor) =>
                              setNovoAssinante((anterior) => ({
                                ...anterior,
                                usuarioId: valor?.id ?? null,
                                email: valor?.email ?? "",
                              }))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Selecionar usuário"
                                size="small"
                                sx={{
                                  minWidth: 220,

                                  "& .MuiOutlinedInput-root": {
                                    color: "var(--ig-t-ffffff)",
                                    backgroundColor: "var(--ig-f-2a2a2a)",

                                    "& fieldset": {
                                      borderColor: "var(--ig-b-444444)",
                                    },
                                  },

                                  "& input": {
                                    color: "var(--ig-t-ffffff)",
                                  },
                                }}
                              />
                            )}
                            sx={{
                              minWidth: 220,
                            }}
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            borderBottom: "1px solid var(--ig-b-333333)",
                            py: 0.5,
                          }}
                        >
                          <TextField
                            size="small"
                            fullWidth
                            type="email"
                            value={novoAssinante.email}
                            placeholder="E-mail do assinante"
                            onChange={(e) =>
                              setNovoAssinante((anterior) => ({
                                ...anterior,
                                email: e.target.value,
                              }))
                            }
                            sx={{
                              minWidth: 220,

                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "var(--ig-f-2a2a2a)",
                                color: "var(--ig-t-ffffff)",

                                "& fieldset": {
                                  borderColor: "var(--ig-b-444444)",
                                },
                              },

                              "& input": {
                                color: "var(--ig-t-ffffff)",
                              },
                            }}
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            borderBottom: "1px solid var(--ig-b-333333)",
                            py: 0.5,
                          }}
                        >
                          <FormControl size="small" fullWidth>
                            <Select
                              value={novoAssinante.status}
                              onChange={(e) =>
                                setNovoAssinante((anterior) => ({
                                  ...anterior,
                                  status: e.target.value,
                                }))
                              }
                              sx={{
                                color: "var(--ig-t-ffffff)",
                                backgroundColor: "var(--ig-f-2a2a2a)",

                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "var(--ig-b-444444)",
                                },

                                "& .MuiSvgIcon-root": {
                                  color: "var(--ig-t-ffffff)",
                                },
                              }}
                            >
                              {STATUS_ASSINANTE.map((status) => (
                                <MenuItem key={status.value} value={status.value}>
                                  {status.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            borderBottom: "1px solid var(--ig-b-333333)",
                            py: 0.5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleAdicionarAssinante}
                            sx={{
                              backgroundColor: "#1998a8",
                              color: "#fff",

                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },

                              textTransform: "none",
                              fontWeight: "bold",
                              width: "auto",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Adicionar
                          </Button>

                          {(novoAssinante.usuarioId || novoAssinante.email) && (
                            <Tooltip title="Limpar campos">
                              <IconButton
                                size="small"
                                onClick={() => setNovoAssinante(novoAssinanteVazio(formAssinantes))}
                                sx={{
                                  ml: 0.5,
                                  backgroundColor: "#555",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  width: "auto",

                                  "&:hover": {
                                    backgroundColor: "#444",
                                  },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              {/* =================================================
                  BOTÕES
              ================================================= */}

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "flex-end",
                }}
              >
                {/* VISUALIZAÇÃO */}

                {editandoId && somenteLeitura && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleCancelarForm}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",

                        "&:hover": {
                          backgroundColor: "var(--ig-f-255-255-255-a005)",
                        },

                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Fechar
                    </Button>

                    {podeCadastrar && (
                      <Button
                        variant="contained"
                        startIcon={<FaEdit />}
                        onClick={handleHabilitarEdicao}
                        sx={{
                          width: "auto",
                          backgroundColor: "#1998a8",

                          "&:hover": {
                            backgroundColor: "#1565c0",
                          },

                          fontWeight: "bold",
                          py: 1,
                          px: 3,
                        }}
                      >
                        Editar
                      </Button>
                    )}

                    {podeExcluir && (
                      <Button
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleConfirmarDelete("alcada", editandoId)}
                        sx={{
                          width: "auto",
                          backgroundColor: "#d32f2f",

                          "&:hover": {
                            backgroundColor: "#c62828",
                          },

                          fontWeight: "bold",
                          py: 1,
                          px: 3,
                        }}
                      >
                        Excluir
                      </Button>
                    )}
                  </>
                )}

                {/* EDIÇÃO / CRIAÇÃO */}

                {(!editandoId || !somenteLeitura) && (
                  <>
                    {editandoId && (
                      <Button
                        variant="outlined"
                        startIcon={<FaTimes />}
                        onClick={handleCancelarForm}
                        sx={{
                          width: "auto",
                          borderColor: "var(--ig-b-555555)",
                          color: "var(--ig-t-cccccc)",

                          "&:hover": {
                            backgroundColor: "var(--ig-f-255-255-255-a005)",
                          },

                          fontWeight: "bold",
                          px: 3,
                        }}
                      >
                        Cancelar
                      </Button>
                    )}

                    {podeCadastrar && (
                      <Button
                        variant="contained"
                        startIcon={<FaSave />}
                        onClick={handleSalvar}
                        sx={{
                          width: "auto",
                          backgroundColor: "#2e7d32",

                          "&:hover": {
                            backgroundColor: "#1b5e20",
                          },

                          fontWeight: "bold",
                          py: 1,
                          px: 4,
                        }}
                      >
                        {editandoId ? "Atualizar" : "Cadastrar"}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* =======================================================
          CONFIRMAÇÃO DE EXCLUSÃO
      ======================================================= */}

      <Dialog
        open={confirmDelete.aberto}
        onClose={() =>
          setConfirmDelete({
            aberto: false,
            tipo: null,
            alcadaId: null,
            uid: null,
          })
        }
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>
            {confirmDelete.tipo === "alcada"
              ? "Deseja excluir esta alçada de assinaturas e todos os seus assinantes?"
              : "Deseja remover este usuário da alçada de assinaturas?"}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            color="inherit"
            onClick={() =>
              setConfirmDelete({
                aberto: false,
                tipo: null,
                alcadaId: null,
                uid: null,
              })
            }
          >
            Cancelar
          </Button>

          <Button variant="contained" color="error" onClick={handleExcluirConfirmado}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* =======================================================
          SNACKBAR
      ======================================================= */}

      <Snackbar
        open={snack.aberto}
        autoHideDuration={3500}
        onClose={() =>
          setSnack((anterior) => ({
            ...anterior,
            aberto: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity={snack.severity}
          onClose={() =>
            setSnack((anterior) => ({
              ...anterior,
              aberto: false,
            }))
          }
          variant="filled"
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
